<template>
    <authenticated-layout>
        <v-card class="pa-6">
            <v-card-title
                class="d-flex justify-space-between align-center pa-0 pb-2"
            >
                <v-btn
                    variant="flat"
                    size="small"
                    color="primary"
                    @click="onOpenModal()"
                >
                    {{ $t("usersIndex.Add a new one") }}
                </v-btn>
            </v-card-title>

            <div class="overflow-x-auto">
                <table>
                    <tr>
                        <th
                            v-for="(header, index) in headers"
                            :key="`table-header-${index}`"
                            class="text-left whitespace-nowrap"
                        >
                            {{ header }}
                        </th>
                    </tr>

                    <tr v-for="row in users" :key="`user-${row.id}`">
                        <td>{{ row.id }}</td>
                        <td>{{ row.pd_id }}</td>
                        <td>{{ row.name }}</td>
                        <td>{{ row.email }}</td>
                        <td>{{ row.phone }}</td>
                        <td>{{ row.role }}</td>
                        <td><img v-if="row.signature_photo_url" width="70" height="30" :src="row.signature_photo_url" alt="Signature"/></td>
                        <td class="text-right">
                            <v-btn
                                variant="flat"
                                size="x-small"
                                color="primary"
                                @click="onEditRow(row)"
                            >
                                {{ $t("usersIndex.Edit") }}
                            </v-btn>

                            <v-btn
                                variant="flat"
                                size="x-small"
                                class="ml-2"
                                color="error"
                                @click="onDeleteRow(row.id)"
                            >
                                {{ $t("usersIndex.Delete") }}
                            </v-btn>
                        </td>
                    </tr>
                </table>
            </div>

            <v-dialog
                v-model="isModalOpen"
                max-width="700"
                persistent
                scrollable
            >
                <v-card>
                    <v-card-title class="text-subtitle-1">
                        {{
                            isOnEditMode
                                ? $t("usersIndex.Edit")
                                : $t("usersIndex.Add a new one")
                        }}
                    </v-card-title>

                    <v-divider />

                    <v-card-text style="max-height: 600px">
                        <div
                            v-for="(item, index) in form"
                            :key="index"
                            v-show="index !== 'id' && index !== 'signature' && index !== 'signature_photo_url' && index !== 'password' && index !== 'max_discount_pv' && index !== 'max_discount_hp' && index !== 'max_premium_pv' && index !== 'max_premium_hp'"
                        >
                            <div v-if="index !== 'id' && index !== 'password'">
                                <v-select
                                    v-if="index === 'role'"
                                    v-model="form[index]"
                                    :label="$t('usersIndex.Select Role')"
                                    :items="[
                                        {
                                            title: $t('usersIndex.admin'),
                                            value: 'admin',
                                        },
                                        {
                                            title: $t('usersIndex.manager'),
                                            value: 'manager',
                                        },
                                        {
                                            title: $t('usersIndex.sales'),
                                            value: 'sales',
                                        },
                                    ]"
                                    variant="outlined"
                                    density="compact"
                                    type="input"
                                    :error-messages="
                                        errors[index] ? [errors[index][0]] : []
                                    "
                                />

                                <v-text-field
                                    v-else
                                    v-model="form[index]"
                                    :label="formatInputLabel(index)"
                                    variant="outlined"
                                    density="compact"
                                    type="input"
                                    :error-messages="
                                        errors[index] ? [errors[index][0]] : []
                                    "
                                />
                            </div>
                        </div>

                        <div>
                            <v-text-field
                                v-model="form.password"
                                :label="$t('usersIndex.Password')"
                                variant="outlined"
                                density="compact"
                                type="password"
                                min="0"
                                :error-messages="
                                    errors.password ? [errors.password[0]] : []
                                "
                            />
                        </div>

                        <div>
                            <v-text-field
                                v-model="form.max_discount_pv"
                                label="Max Discount PV*"
                                variant="outlined"
                                density="compact"
                                type="number"
                                min="0"
                                :error-messages="
                                    errors.max_discount_pv ? [errors.max_discount_pv[0]] : []
                                "
                            />
                        </div>

                        <di>
                            <v-text-field
                                v-model="form.max_discount_hp"
                                label="Max Discount HP*"
                                variant="outlined"
                                density="compact"
                                type="number"
                                min="0"
                                :error-messages="
                                    errors.max_discount_hp ? [errors.max_discount_hp[0]] : []
                                "
                            />
                        </di>

                        <div>
                            <v-text-field
                                v-model="form.max_premium_pv"
                                label="Max Premium PV*"
                                variant="outlined"
                                density="compact"
                                type="number"
                                min="0"
                                :error-messages="
                                    errors.max_premium_pv ? [errors.max_premium_pv[0]] : []
                                "
                            />
                        </div>

                        <div>
                            <v-text-field
                                v-model="form.max_premium_hp"
                                label="Max Premium HP*"
                                variant="outlined"
                                density="compact"
                                type="number"
                                :error-messages="
                                    errors.max_premium_hp ? [errors.max_premium_hp[0]] : []
                                "
                            />
                        </div>

                        <div class="mb-5">
                            <v-label>* Empty value for no limit</v-label>
                        </div>

                        <div>
                            <v-file-input
                                v-model="form.signature"
                                label="Signature"
                                variant="outlined"
                                density="compact"
                                type="file"
                                accept="image/*"
                                prepend-icon="mdi-camera"
                                :error-messages="
                                    errors.signature ? [errors.signature[0]] : []
                                "
                            />

                            <img v-if="signaturePhotoUrl" class="m-2" width="70" height="30" :src="signaturePhotoUrl" alt="Signature"/>
                        </div>
                    </v-card-text>

                    <v-divider />

                    <v-card-actions>
                        <v-spacer />

                        <v-btn
                            variant="flat"
                            size="small"
                            @click="onCloseModal()"
                        >
                            {{ $t("usersIndex.Cancel") }}
                        </v-btn>

                        <v-btn
                            v-show="!isOnEditMode"
                            color="secondary"
                            variant="flat"
                            size="small"
                            @click="onSave()"
                        >
                            {{ $t("usersIndex.Save") }}
                        </v-btn>

                        <v-btn
                            v-show="isOnEditMode"
                            color="secondary"
                            variant="flat"
                            size="small"
                            @click="onUpdate()"
                        >
                            {{ $t("usersIndex.Update") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from "@/Layouts/Authenticated";

export default {
    components: {
        AuthenticatedLayout,
    },
    props: ["users", "signature", "errors"],
    data() {
        return {
            headers: [
                "ID",
                "Pipedrive ID",
                this.$t("usersIndex.Name"),
                this.$t("usersIndex.Email"),
                this.$t("usersIndex.Phone"),
                this.$t("usersIndex.Role"),
                "Signature",
                "",
            ],
            isModalOpen: false,
            isOnEditMode: false,
            form: {
                id: null,
                pd_id: null,
                name: null,
                email: null,
                phone: null,
                max_discount_pv: null,
                max_discount_hp: null,
                max_premium_pv: null,
                max_premium_hp: null,
                role: "",
                signature: null,
            },
            signaturePhotoUrl: null,
        };
    },
    methods: {
        onCloseModal() {
            this.isModalOpen = false;
            this.isOnEditMode = false;

            this.onResetForm();
        },
        onOpenModal() {
            this.isModalOpen = true;
        },
        onDeleteRow(id) {
            if (!confirm(this.$t("usersIndex.Are you sure you want to delete this user #" + id))) {
                return;
            }

            const deleteRoute = this.route('delete-user', { id: id });

            this.$inertia.delete(deleteRoute, {
                onSuccess: () => {
                    this.$toast.open({
                        message: 'User deleted successfully.',
                        type: 'success',
                    });
                },
                onError: () => {
                    this.$toast.open({
                        message: 'Error deleting user.',
                        type: 'error',
                    });
                }
            });
        },
        onEditRow(row) {
            const data = _.pick(row, [
                "id",
                "pd_id",
                "name",
                "email",
                "phone",
                "role",
                'signature',
                'signature_photo_url',
                "max_discount_pv",
                "max_discount_hp",
                "max_premium_pv",
                "max_premium_hp",
            ]);

            this.form = Object.assign({}, data);
            this.isOnEditMode = true;
            this.signaturePhotoUrl = data.signature_photo_url;

            this.onOpenModal();
        },
        onSave() {
            this.$inertia.post(this.route('create-user'), this.form);

            this.onCloseModal();
        },
        onUpdate() {
            this.$inertia.post(this.route('edit-user'), this.form);

            this.onCloseModal();
        },
        onResetForm() {
            for (let i in this.form) {
                this.form[i] = null;
            }
        },
        formatInputLabel(value) {
            if (value === "pd_id") {
                return "Pipedrive ID";
            } else {
                return (value.charAt(0).toUpperCase() + value.slice(1)).replace(
                    "_",
                    " "
                );
            }
        },
    },
};
</script>

<style scoped>
table {
    width: 100%;
    table-layout: fixed;
    font-size: 12px;
}

@media screen and (max-width: 1024px) {
    table {
        width: auto;
    }
}

th {
    padding: 10px 0px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    box-sizing: border-box;
    padding-left: 10px;
    border-bottom: 1px solid #aaa;
}

td {
    text-align: left;
    box-sizing: border-box;
    padding: 5px 0px;
    padding-left: 10px;
    border-bottom: 1px solid #ccc;
}
</style>
